import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import {
  getAllAssessments,
  getAladdinExportConfigurations,
  getCountries,
  getCountryScreenersByCountryId,
  updateAladdinExportConfiguration,
} from "../services/APIClient";

const emptyForm = {
  enabled: false,
  availableFrom: "",
  availableUntil: "",
  unavailableMessage: "",
  requirePublishedScores: true,
};

const irelandDateFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Dublin",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const irelandParts = (date) => Object.fromEntries(
  irelandDateFormatter.formatToParts(date)
    .filter(({ type }) => type !== "literal")
    .map(({ type, value }) => [type, Number(value)])
);

export const toIrelandInput = (value) => {
  if (!value) return "";
  const parts = irelandParts(new Date(value));
  const pad = (part) => String(part).padStart(2, "0");
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
};

export const irelandInputToUtc = (value) => {
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) throw new RangeError("Invalid Ireland date/time.");
  const [, year, month, day, hour, minute] = match.map(Number);
  const target = Date.UTC(year, month - 1, day, hour, minute, 0);
  let instant = target;
  for (let index = 0; index < 3; index += 1) {
    const represented = irelandParts(new Date(instant));
    instant += target - Date.UTC(
      represented.year,
      represented.month - 1,
      represented.day,
      represented.hour,
      represented.minute,
      represented.second
    );
  }
  return new Date(instant).toISOString();
};

const stateVariant = {
  available: "success",
  scheduled: "info",
  waiting_for_scores: "warning",
  expired: "secondary",
  disabled: "secondary",
  configuration_error: "danger",
};

const isAcademicYearValid = (value) => {
  const match = String(value).trim().match(/^(\d{2}|\d{4})\s*[-/]\s*(\d{2}|\d{4})$/);
  if (!match) return false;
  const start = Number(match[1].length === 4 ? match[1] : `20${match[1]}`);
  const end = Number(match[2].length === 4 ? match[2] : `${String(start).slice(0, 2)}${match[2]}`);
  return end === start + 1;
};

const ACADEMIC_YEAR_START_MONTH = 8;
const ACADEMIC_YEAR_START_DAY = 1;

const formatAcademicYear = (startYear) =>
  `${String(startYear % 100).padStart(2, "0")}-${String((startYear + 1) % 100).padStart(2, "0")}`;

const currentAcademicYearStart = () => {
  const parts = irelandParts(new Date());
  const afterBoundary =
    parts.month > ACADEMIC_YEAR_START_MONTH ||
    (parts.month === ACADEMIC_YEAR_START_MONTH && parts.day >= ACADEMIC_YEAR_START_DAY);
  return afterBoundary ? parts.year : parts.year - 1;
};

// One year back through four years ahead, so staff can schedule well in advance.
const academicYearOptions = (() => {
  const currentStart = currentAcademicYearStart();
  const options = [];
  for (let offset = -1; offset <= 4; offset += 1) {
    options.push(formatAcademicYear(currentStart + offset));
  }
  return options;
})();

function AladdinExportActivationManager() {
  const [countries, setCountries] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [countryScreeners, setCountryScreeners] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [assessmentId, setAssessmentId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const academicYearValid = isAcademicYearValid(academicYear);

  useEffect(() => {
    Promise.all([getCountries(), getAllAssessments()])
      .then(([countryData, assessmentData]) => {
        setCountries(Array.isArray(countryData) ? countryData : countryData?.data || []);
        setAssessments(Array.isArray(assessmentData) ? assessmentData : assessmentData?.data || []);
      })
      .catch(() => setError("Failed to load Aladdin Export configuration choices."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let active = true;
    setAssessmentId("");
    setCountryScreeners([]);
    setConfigurations([]);
    if (!countryId) return;
    setLoading(true);
    Promise.all([
      getCountryScreenersByCountryId(countryId),
      academicYearValid ? getAladdinExportConfigurations(countryId, academicYear) : Promise.resolve([]),
    ])
      .then(([screeners, configs]) => {
        if (!active) return;
        setCountryScreeners(Array.isArray(screeners) ? screeners : []);
        setConfigurations(Array.isArray(configs) ? configs : []);
      })
      .catch(() => {
        if (active) setError("Failed to load territory Aladdin Export settings.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [countryId, academicYear, academicYearValid]);

  const assessmentById = useMemo(
    () => new Map(assessments.map((assessment) => [assessment.id, assessment])),
    [assessments]
  );
  const availableScreeners = countryScreeners
    .map((mapping) => assessmentById.get(mapping.assessmentid))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
  const selectedConfiguration = configurations.find(
    (configuration) => configuration.assessmentId === assessmentId
  );

  useEffect(() => {
    if (!assessmentId) {
      setForm(emptyForm);
      return;
    }
    if (!selectedConfiguration) {
      setForm(emptyForm);
      return;
    }
    setForm({
      enabled: selectedConfiguration.enabled,
      availableFrom: toIrelandInput(selectedConfiguration.availableFrom),
      availableUntil: toIrelandInput(selectedConfiguration.availableUntil),
      unavailableMessage: selectedConfiguration.unavailableMessage || selectedConfiguration.message || "",
      requirePublishedScores: selectedConfiguration.requirePublishedScores !== false,
    });
  }, [assessmentId, selectedConfiguration]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!countryId || !academicYearValid || !assessmentId || !form.availableFrom || !form.unavailableMessage.trim()) {
      setError("Territory, academic year, screener, activation date and unavailable message are required.");
      return;
    }
    if (form.availableUntil && new Date(form.availableUntil) <= new Date(form.availableFrom)) {
      setError("The deactivation date must be after the activation date.");
      return;
    }
    setSaving(true);
    try {
      const saved = await updateAladdinExportConfiguration(
        countryId,
        academicYear,
        assessmentId,
        {
          enabled: form.enabled,
          availableFrom: irelandInputToUtc(form.availableFrom),
          availableUntil: form.availableUntil ? irelandInputToUtc(form.availableUntil) : null,
          unavailableMessage: form.unavailableMessage.trim(),
          requirePublishedScores: form.requirePublishedScores,
        }
      );
      setConfigurations((current) => [
        ...current.filter((item) => item.assessmentId !== saved.assessmentId),
        saved,
      ]);
      setSuccess("Aladdin Export activation saved.");
    } catch (saveError) {
      setError(saveError.response?.data?.error?.message || "Failed to save Aladdin Export activation.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && countries.length === 0) {
    return <div className="p-3 text-center"><Spinner animation="border" size="sm" /> Loading activation controls...</div>;
  }

  return (
    <Card className="mb-4 border-primary">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Aladdin Export Scheduled Activation</strong>
        <Badge bg="secondary">The Teacher Dashboard navigation switch remains the global control</Badge>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        <Form onSubmit={handleSave}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label htmlFor="aladdin-country">Territory</Form.Label>
              <Form.Select id="aladdin-country" value={countryId} onChange={(event) => setCountryId(event.target.value)}>
                <option value="">Select territory</option>
                {countries.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label htmlFor="aladdin-academic-year">Academic year</Form.Label>
              <Form.Select
                id="aladdin-academic-year"
                value={academicYear}
                onChange={(event) => setAcademicYear(event.target.value)}
              >
                <option value="">Select academic year</option>
                {academicYearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label htmlFor="aladdin-screener">Screener</Form.Label>
              <Form.Select
                id="aladdin-screener"
                value={assessmentId}
                onChange={(event) => setAssessmentId(event.target.value)}
                disabled={!countryId || !academicYearValid}
              >
                <option value="">Select screener</option>
                {availableScreeners.map((assessment) => (
                  <option key={assessment.id} value={assessment.id}>{assessment.name}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {assessmentId && (
            <>
              <Row className="g-3 mt-1">
                <Col md={4}>
                  <Form.Label htmlFor="aladdin-available-from">Available from (Ireland time)</Form.Label>
                  <Form.Control id="aladdin-available-from" type="datetime-local" value={form.availableFrom} onChange={(event) => setField("availableFrom", event.target.value)} />
                </Col>
                <Col md={4}>
                  <Form.Label htmlFor="aladdin-available-until">Available until (optional)</Form.Label>
                  <Form.Control id="aladdin-available-until" type="datetime-local" value={form.availableUntil} onChange={(event) => setField("availableUntil", event.target.value)} />
                </Col>
                <Col md={4} className="d-flex flex-column justify-content-end">
                  <Form.Check type="switch" label={form.enabled ? "Activation enabled" : "Activation disabled"} checked={form.enabled} onChange={(event) => setField("enabled", event.target.checked)} />
                  <Form.Check className="mt-2" type="switch" label="Require published standardised scores" checked={form.requirePublishedScores} onChange={(event) => setField("requirePublishedScores", event.target.checked)} />
                </Col>
              </Row>
              {!form.requirePublishedScores && (
                <Alert variant="danger" className="mt-3">
                  Score-readiness protection is disabled. This setting must only be used for an explicitly approved exceptional release.
                </Alert>
              )}
              <Form.Group className="mt-3">
                <Form.Label>Teacher-facing unavailable message</Form.Label>
                <Form.Control as="textarea" rows={2} value={form.unavailableMessage} onChange={(event) => setField("unavailableMessage", event.target.value)} placeholder="This export is scheduled to open on..." />
              </Form.Group>

              {selectedConfiguration && (
                <div className="mt-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <strong>Effective state</strong>
                    <Badge bg={stateVariant[selectedConfiguration.state] || "secondary"}>{selectedConfiguration.state}</Badge>
                  </div>
                  {selectedConfiguration.state === "waiting_for_scores" && (
                    <Alert variant="warning">
                      The scheduled opening has arrived, but one or more standardised-score calculations are missing or stale. The export will remain unavailable.
                    </Alert>
                  )}
                  {selectedConfiguration.taskReadiness?.length > 0 && (
                    <Table size="sm" bordered responsive>
                      <thead><tr><th>Task</th><th>Readiness</th></tr></thead>
                      <tbody>
                        {selectedConfiguration.taskReadiness.map((task) => (
                          <tr key={task.taskId}>
                            <td>{task.taskName}</td>
                            <td><Badge bg={task.ready ? "success" : "warning"}>{task.ready ? "Ready" : task.reason}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              )}
              <Button type="submit" className="mt-3" disabled={saving}>
                {saving ? "Saving..." : "Save activation"}
              </Button>
            </>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
}

export default AladdinExportActivationManager;
